import { useMemo } from 'react'
import { BulkSheetProperty } from '../bulksheetProperty'
import { PermissionPolicyOption } from '../../../../domain/value-object/PermissionPolicy'
import { intl } from '../../../../i18n'
import { MultiSelectCellEditor } from '../../../containers/BulkSheetView/components/cellEditor/MultiSelectCellEditor'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { ClientSideSelectFilter } from '../../../containers/BulkSheetView/components/filter'
import { useAttachablePermissionPolicyOptions } from './attachablePermissionPolicyOptions'
import objects from '../../../../utils/objects'

export const usePermissionPolicies = (
  field: string = 'permissionPolicyUuids',
  permissionPolicyOptions: PermissionPolicyOption[],
  allPermissionPolicyOptions: PermissionPolicyOption[],
  override?: Pick<
    BulkSheetProperty,
    'editable' | 'cellRendererParams' | 'cellStyle'
  >
): BulkSheetProperty => {
  const selectOptions = useAttachablePermissionPolicyOptions(
    permissionPolicyOptions
  )
  const property = useMemo(() => {
    return {
      field,
      headerName: intl.formatMessage({ id: 'roles.column.policies' }),
      editable: override?.editable,
      width: 600,
      cellEditor: MultiSelectCellEditor,
      cellEditorParams: {
        options: selectOptions,
      },
      cellRenderer: DefaultCellRenderer,
      cellRendererParams: override?.cellRendererParams,
      valueFormatter: params => {
        if (!params.value || params.value.length === 0) return ''
        const selectedPolicies = allPermissionPolicyOptions.filter(p =>
          params.value.includes(p.uuid)
        )
        return selectedPolicies.map(p => p.name).join(',')
      },
      cellStyle: override?.cellStyle,
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        valueGetter: (params: { node; context }) => {
          if (!params.node.data) {
            return undefined
          }
          const row = params.node.data
          const permissionPolicyUuids = objects.getValue(row, field) || []

          return allPermissionPolicyOptions.filter(v =>
            permissionPolicyUuids.includes(v.uuid)
          )
        },
        getValue: (o: PermissionPolicyOption) => o.uuid,
        getLabel: (o: PermissionPolicyOption) => o.name,
        filterChangedCallback: () => {
          return
        },
      },
    }
  }, [selectOptions, permissionPolicyOptions])

  return property
}
